import { useParams, Link } from 'react-router-dom';
import { allComics } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = allComics.find((c) => c.id === id);

  if (!comic) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-foreground-300 text-5xl"></i>
          </div>
          <h1 className="text-foreground-900 font-heading text-xl mb-2">漫画未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">抱歉，您查找的漫画不存在或已下架</p>
          <Link
            to="/"
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = allComics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 4);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 布丁漫画全集下拉式连续阅读`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.chapters[0]?.updateDate || '2026-01-01',
            dateModified: comic.updateDate,
            url: `https://budingmanhua.com/comic/${comic.id}`,
            publisher: {
              '@type': 'Organization',
              name: '布丁漫画网',
            },
          }),
        }}
      />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '分类浏览', path: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <section className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200 sticky top-24">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 布丁漫画下拉式连续阅读全集`}
                    title={`${comic.title} 布丁漫画下拉式免费阅读全本`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-foreground-600 text-sm">{comic.author} · 著</span>
                  <span className="inline-flex items-center gap-1 text-accent-500 text-sm font-medium">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                    {comic.rating}
                  </span>
                  <span className="text-foreground-400 text-sm">
                    {comic.views.toLocaleString()} 次阅读
                  </span>
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      comic.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-8 border border-background-200/70">
                  <h4 className="text-foreground-900 font-medium text-sm mb-3">作品简介</h4>
                  <p className="text-foreground-600 text-sm leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mb-10">
                  <Link
                    to={`/comic/${comic.id}/read`}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-full font-medium text-sm text-center whitespace-nowrap cursor-pointer transition-colors"
                  >
                    开始下拉式阅读
                    <span className="ml-2"><i className="ri-arrow-down-line"></i></span>
                  </Link>
                  <Link
                    to={`/comic/${comic.id}/read?chapter=1`}
                    className="bg-background-100 hover:bg-background-200/80 text-foreground-700 border border-background-200/70 px-8 py-3 rounded-full font-medium text-sm text-center whitespace-nowrap cursor-pointer transition-colors"
                  >
                    从第一话开始
                  </Link>
                </div>

                <div>
                  <h2 className="font-heading text-xl text-foreground-900 mb-4">
                    章节目录（共 {comic.chapters.length} 话）
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-96 overflow-y-auto pr-2">
                    {comic.chapters.map((chapter) => (
                      <Link
                        key={chapter.id}
                        to={`/comic/${comic.id}/read?chapter=${chapter.number}`}
                        className="block px-3 py-2 rounded-lg text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 border border-background-200/70 transition-all duration-200 whitespace-nowrap text-center cursor-pointer"
                      >
                        {chapter.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h2 className="font-heading text-xl text-foreground-900 mb-6">同类推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-shop="">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/comic/${rc.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                        <img
                          src={rc.cover}
                          alt={`${rc.title} - 布丁漫画下拉式免费阅读`}
                          title={`${rc.title} 布丁漫画下拉式免费阅读推荐`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="text-foreground-900 font-medium text-sm truncate group-hover:text-primary-500 transition-colors">
                        {rc.title}
                      </h3>
                      <span className="text-foreground-500 text-xs">{rc.author}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}